import { memo, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Button,
  TextInput,
  Select,
  ScrollArea,
  Group,
  Grid,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import type {
  UpdateUserPayload,
  UserInfo,
} from '../../services/userManager';
import { professions } from '../../data/professions';
import { discipline } from '../../data/discipline';

interface EditUserModalProps {
  opened: boolean;
  onClose: () => void;
  user: UserInfo | null;
  saving?: boolean;
  onSave: (payload: UpdateUserPayload) => void | Promise<void>;
}

type EditUserFormValues = {
  email: string;
  type: string;
  first_name: string;
  last_name: string;
  full_name: string;
  title: string;
  role: string;
  profession: string | null;
  disciple: string | null;
  contract: string;
  fiscal_code: string;
  order_of_psychologists: string;
  order_number: string;
  region: string;
  address: string;
  vat_number: string;
  sdi_number: string;
  website: string;
  is_public: string | null;
};

const emptyValues = (): EditUserFormValues => ({
  email: '',
  type: '',
  first_name: '',
  last_name: '',
  full_name: '',
  title: '',
  role: '',
  profession: null,
  disciple: null,
  contract: '',
  fiscal_code: '',
  order_of_psychologists: '',
  order_number: '',
  region: '',
  address: '',
  vat_number: '',
  sdi_number: '',
  website: '',
  is_public: null,
});

const toFormValues = (user: UserInfo | null): EditUserFormValues => {
  if (!user) return emptyValues();
  return {
    email: user.email ?? '',
    type: user.type != null ? String(user.type) : '',
    first_name: user.first_name != null ? String(user.first_name) : '',
    last_name: user.last_name != null ? String(user.last_name) : '',
    full_name: user.full_name != null ? String(user.full_name) : '',
    title: user.title != null ? String(user.title) : '',
    role: user.role != null ? String(user.role) : '',
    profession:
      user.profession != null ? String(user.profession) : null,
    disciple: user.disciple != null ? String(user.disciple) : null,
    contract: user.contract != null ? String(user.contract) : '',
    fiscal_code: user.fiscal_code != null ? String(user.fiscal_code) : '',
    order_of_psychologists:
      user.order_of_psychologists != null
        ? String(user.order_of_psychologists)
        : '',
    order_number: user.order_number != null ? String(user.order_number) : '',
    region: user.region != null ? String(user.region) : '',
    address: user.address != null ? String(user.address) : '',
    vat_number: user.vat_number != null ? String(user.vat_number) : '',
    sdi_number: user.sdi_number != null ? String(user.sdi_number) : '',
    website: user.website != null ? String(user.website) : '',
    is_public:
      user.is_public === 0 || user.is_public === 1
        ? String(user.is_public)
        : user.is_public == null
          ? null
          : String(user.is_public),
  };
};

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const toPayload = (values: EditUserFormValues): UpdateUserPayload => {
  const profession =
    values.profession == null || values.profession === ''
      ? null
      : Number(values.profession);
  const disciple =
    values.disciple == null || values.disciple === ''
      ? null
      : Number(values.disciple);
  const isPublic =
    values.is_public == null || values.is_public === ''
      ? null
      : Number(values.is_public);

  return {
    email: values.email.trim(),
    type: emptyToNull(values.type),
    first_name: emptyToNull(values.first_name),
    last_name: emptyToNull(values.last_name),
    full_name: emptyToNull(values.full_name),
    title: emptyToNull(values.title),
    role: emptyToNull(values.role),
    profession: Number.isFinite(profession as number) ? profession : null,
    disciple: Number.isFinite(disciple as number) ? disciple : null,
    contract: emptyToNull(values.contract),
    fiscal_code: emptyToNull(values.fiscal_code),
    order_of_psychologists: emptyToNull(values.order_of_psychologists),
    order_number: emptyToNull(values.order_number),
    region: emptyToNull(values.region),
    address: emptyToNull(values.address),
    vat_number: emptyToNull(values.vat_number),
    sdi_number: emptyToNull(values.sdi_number),
    website: emptyToNull(values.website),
    is_public: isPublic === 0 || isPublic === 1 ? isPublic : null,
  };
};

const professionOptions = professions.map(item => ({
  value: String(item.value),
  label: item.label,
}));

export const EditUserModal = memo<EditUserModalProps>(
  ({ opened, onClose, user, saving = false, onSave }) => {
    const form = useForm<EditUserFormValues>({
      initialValues: emptyValues(),
      validate: {
        email: value =>
          !value.trim() ? 'Email obbligatoria' : null,
      },
    });

    useEffect(() => {
      if (opened) {
        form.setValues(toFormValues(user));
        form.clearErrors();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when modal opens / user changes
    }, [opened, user]);

    const discipleOptions = useMemo(() => {
      const professionId = form.values.profession;
      const lists: Array<{ label: string; value: number }> = [];
      if (professionId && discipline[professionId]) {
        lists.push(...discipline[professionId]);
      } else {
        for (const key of Object.keys(discipline)) {
          lists.push(...(discipline[key] ?? []));
        }
      }
      return lists.map(item => ({
        value: String(item.value),
        label: item.label,
      }));
    }, [form.values.profession]);

    const handleSubmit = (values: EditUserFormValues) => {
      void onSave(toPayload(values));
    };

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title='Modifica utente'
        size='xl'
      >
        <ScrollArea.Autosize mah='80vh' offsetScrollbars>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap='md'>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Email'
                    required
                    {...form.getInputProps('email')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label='Tipo' {...form.getInputProps('type')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Nome'
                    {...form.getInputProps('first_name')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Cognome'
                    {...form.getInputProps('last_name')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Nome completo'
                    {...form.getInputProps('full_name')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label='Titolo' {...form.getInputProps('title')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label='Ruolo' {...form.getInputProps('role')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label='Pubblico'
                    clearable
                    data={[
                      { value: '1', label: 'Sì (1)' },
                      { value: '0', label: 'No (0)' },
                    ]}
                    {...form.getInputProps('is_public')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label='Professione'
                    clearable
                    searchable
                    data={professionOptions}
                    {...form.getInputProps('profession')}
                    onChange={value => {
                      form.setFieldValue('profession', value);
                      form.setFieldValue('disciple', null);
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label='Disciplina'
                    clearable
                    searchable
                    data={discipleOptions}
                    {...form.getInputProps('disciple')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Contratto'
                    {...form.getInputProps('contract')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Codice fiscale'
                    {...form.getInputProps('fiscal_code')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Ordine psicologi'
                    {...form.getInputProps('order_of_psychologists')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='N. ordine'
                    {...form.getInputProps('order_number')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Regione'
                    {...form.getInputProps('region')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='Indirizzo'
                    {...form.getInputProps('address')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label='P. IVA'
                    {...form.getInputProps('vat_number')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label='SDI' {...form.getInputProps('sdi_number')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12 }}>
                  <TextInput
                    label='Website'
                    {...form.getInputProps('website')}
                  />
                </Grid.Col>
              </Grid>

              <Group justify='flex-end' gap='sm'>
                <Button variant='default' onClick={onClose} disabled={saving}>
                  Annulla
                </Button>
                <Button type='submit' loading={saving}>
                  Salva
                </Button>
              </Group>
            </Stack>
          </form>
        </ScrollArea.Autosize>
      </Modal>
    );
  }
);

EditUserModal.displayName = 'EditUserModal';
